import axios from "axios";
import { toSnakeCase, toCamelCase } from "./data-format";
import { getToken, removeToken, setToken, getUserInfo, removeAll } from "./auth";
import { refresh } from "@/api/security";
import { Message } from "element-ui";
import router from "@/router";
import store from "@/store";

const http = axios.create({
    baseURL: process.env.VUE_APP_BASE_API,
    timeout: 30000,
});

// 定時刷新、併發鎖、請求佇列全域變數
let tokenRefreshTimer = null;
let isRefreshing = false;
let pendingRequestQueue = [];
// 根據後端 accessToken 過期時長調整，提前 5 分鐘刷新
const REFRESH_INTERVAL = 25 * 60 * 1000;

/** 開啟定時靜默刷新 token */
function startAutoRefreshToken() {
    stopAutoRefreshToken();
    tokenRefreshTimer = setInterval(async () => {
        const refreshToken = getUserInfo("refreshToken");
        const accessToken = getToken();
        if (!refreshToken || !accessToken) {
            stopAutoRefreshToken();
            return;
        }
        // 正在刷新中則跳過本次定時任務，避免並發
        if (isRefreshing) return;

        try {
            const res = await refresh({ refreshToken }, { unformat: true, _retry: true });
            setToken(res.data.accessToken);
            // 定時刷新成功，同步喚醒所有排隊請求
            pendingRequestQueue.forEach((item) => item.resolve(res.data.accessToken));
            pendingRequestQueue = [];
        } catch (err) {
            stopAutoRefreshToken();
            clearLoginInfo();
        }
    }, REFRESH_INTERVAL);
}

/** 關閉定時刷新 */
function stopAutoRefreshToken() {
    if (tokenRefreshTimer) {
        clearInterval(tokenRefreshTimer);
        tokenRefreshTimer = null;
    }
}

/** 統一清空登入資訊並跳轉登入介面 */
function clearLoginInfo() {
    stopAutoRefreshToken();
    removeToken();
    removeAll();
    window.sessionStorage.clear();
    // 拒絕所有等待中的請求，防止介面永久卡死
    pendingRequestQueue.forEach(({ reject }) => reject("token 已過期"));
    pendingRequestQueue = [];
    isRefreshing = false;
    // 當前不在登入頁才跳轉，避免重複跳轉
    if (router.currentRoute.path !== "/login") {
        router.push({ path: "/login" });
    }
}

http.interceptors.request.use(
    (config) => {
        // 確保 headers 物件存在，避免空指標報錯
        config.headers = config.headers || {};

        const token = getToken();
        if (token != undefined) {
            config.headers.Authorization = `Bearer ${token}`;
            // 有 token 且計時器未啟動時，自動啟動定時刷新（解決頁面重整後定時器丟失）
            if (!tokenRefreshTimer) {
                startAutoRefreshToken();
            }
        }

        // 語言參數兜底，避免 store 異常時請求頭出錯
        config.headers["accept-language"] = store.getters.language ?? "zh-TW";

        if (!config.unformat) {
            // 僅普通物件執行欄位轉換，跳過 FormData、Blob 等特殊類型
            if (config.params && config.params.constructor === Object) {
                config.params = toSnakeCase(config.params);
            }
            if (config.data && config.data.constructor === Object) {
                config.data = toSnakeCase(config.data);
            }
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

http.interceptors.response.use(
    (response) => {
        // 精準匹配登入介面，啟動定時刷新
        if (response.config.url?.endsWith("/login")) {
            startAutoRefreshToken();
        }
        // 兼容帶 charset 的 json 響應，確保駝峰轉換正常執行
        if (response.headers["content-type"]?.includes("application/json")) {
            response.data = toCamelCase(response.data);
        }
        return response;
    },
    async (error) => {
        if (!error.response) {
            Message.error("網路連線異常，請檢查網路");
            return Promise.reject(error);
        }

        const { status } = error.response;
        const originConfig = error.config;

        // ========== 新增：refresh 接口返回 401 優先處理 ==========
        // 帶 _retry 標記的請求就是刷新 token 接口，401 代表 refreshToken 已失效（其他設備登錄），直接強制登出
        if (status === 401 && originConfig._retry) {
            clearLoginInfo();
            return Promise.reject(error);
        }

        // 401 Token 過期自動刷新邏輯（普通業務請求）
        if (status === 401 && !originConfig._retry) {
            const refreshToken = getUserInfo("refreshToken");
            if (!refreshToken) {
                clearLoginInfo();
                return Promise.reject(error);
            }

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    pendingRequestQueue.push({
                        resolve: (newToken) => {
                            originConfig.headers = originConfig.headers || {};
                            originConfig.headers.Authorization = `Bearer ${newToken}`;
                            resolve(http(originConfig));
                        },
                        reject
                    });
                });
            }

            isRefreshing = true;
            originConfig._retry = true;
            try {
                // 標記 _retry 避免 refresh 自身進入重試死循環
                const res = await refresh({ refreshToken }, { unformat: true, _retry: true });
                const newAccessToken = res.data.accessToken;
                setToken(newAccessToken);
                // 喚醒所有排隊請求
                pendingRequestQueue.forEach((item) => item.resolve(newAccessToken));
                pendingRequestQueue = [];

                originConfig.headers = originConfig.headers || {};
                originConfig.headers.Authorization = `Bearer ${newAccessToken}`;
                return http(originConfig);
            } catch {
                clearLoginInfo();
                return Promise.reject(error);
            } finally {
                isRefreshing = false;
            }
        }

        // 保留你原有完整 500 錯誤處理邏輯，增加空值防護
        else if (error.response.status === 500) {
            const respData = error.response.data || {};
            const msg = respData.message;
            if (msg && msg.indexOf("($") > 0) {
                let regex = new RegExp("(?<=\\(\\$)(.+?)(?=\\))", "g");
                let matchArr = msg.match(regex);
                if (matchArr && matchArr[0]) {
                    let key = matchArr[0];
                    if (key == "refreshToken") console.log(msg);
                    else Message.error(msg);
                } else {
                    console.log(msg);
                }
            } else {
                console.log(msg);
            }
        }
        // 保留你原有全部業務錯誤碼判斷，增加空值防護
        else if (
            error.response.data?.error_code == "INVALID_USER_ACCOUNT" ||
            error.response.data?.error_code == "INVALID_PARENT_PASSWORD" ||
            error.response.data?.error_code == "INVALID_STUDENT_PASSWORD" ||
            error.response.data?.error_code == "SUBSCRIPTION_EXPIRED_ERROR" ||
            error.response.data?.error_code == "NO_SUBSCRIPTION_ERROR" ||
            error.response.data?.error_code == "SUBSCRIPTION_NOT_START_ERROR" ||
            error.response.data?.error_code == "AUTH_ERROR"
        ) {
            console.log(error.response.data?.error_message);
        } else {
            Message.error(error.response.data?.error_message);
        }

        return Promise.reject(error);
    }
);

export default http;